/**
 * EU online withdrawal function (Directive 2011/83/EU Art 11a). Wire types for the
 * `withdrawal.getContext` / `withdrawal.submit` backend actions.
 */
export interface WithdrawalContractView {
    id: string;
    planName?: string;
    status: string;
    /** False only when the status proves no charge was made. */
    charged: boolean;
}

export interface WithdrawalContext {
    enabled: boolean;
    name: string;
    email: string;
    contracts: WithdrawalContractView[];
    supportEmail: string;
    /** Only the workspace owner may submit; other members see ownerEmail. */
    canWithdraw: boolean;
    ownerEmail?: string;
}

export type WithdrawalContractOutcome = 'cancelled' | 'already_ending' | 'cancel_failed';

export interface WithdrawalSubmitInput {
    name: string;
    email: string;
    subscriptionIds: string[];
}

export interface WithdrawalResult {
    success: true;
    statementId: string;
    submittedAt: string;
    contracts: Array<WithdrawalContractView & { outcome: WithdrawalContractOutcome }>;
    cancelledAll: boolean;
    anyCharged: boolean;
    receiptQueued: boolean;
    supportEmail: string;
    /** True when the contract already had a statement: the stored record, no second receipt. */
    duplicate?: boolean;
}

const EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

export type WithdrawalFormError = 'name' | 'email' | 'contracts';

/** First problem with the form, or null. Mirrors the server's validation. */
export function validateWithdrawalForm(input: WithdrawalSubmitInput): WithdrawalFormError | null {
    if (!input.name.trim() || input.name.trim().length > 200) return 'name';
    const email = input.email.trim();
    if (!email || email.length > 254 || !EMAIL_RE.test(email)) return 'email';
    if (input.subscriptionIds.length === 0) return 'contracts';
    return null;
}

/** The section shows only when the server says it is on AND there is a contract to withdraw from. */
export function shouldShowWithdrawal(ctx: WithdrawalContext | undefined): ctx is WithdrawalContext {
    return !!ctx && ctx.enabled && ctx.contracts.length > 0;
}

/**
 * The submission instant for the done step, in the user's language but always in UTC with the zone stated,
 * so it is the same instant, to the second, as the receipt email ("2026-11-04 20:31:42 UTC").
 * Example (en): "4 Nov 2026, 20:31:42 UTC". An unparseable value is shown as given rather than hidden.
 */
export function formatWithdrawalInstant(iso: string, locale: string | undefined): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const opts: Intl.DateTimeFormatOptions = {
        timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    };
    let text: string;
    try {
        text = new Intl.DateTimeFormat(locale || 'en-GB', opts).format(d);
    } catch {
        text = new Intl.DateTimeFormat('en-GB', opts).format(d);
    }
    return `${text} UTC`;
}
